<template>
  <div class="shop-decoration-container">
    <!-- 顶部导航区 -->
    <div class="top-bar">
      <div class="page-header">
        <h1 class="page-title">店铺装修</h1>
        <p class="page-desc">自定义您的小程序程序首页展示效果</p>
      </div>

      <div class="action-controls">
        <!-- 操作按钮区 -->
        <div class="action-buttons">
          <el-button
              type="primary"
              @click="openShopSelection"
              class="shop-select-btn"
          >
            <i class="el-icon-store"></i> 选择应用店铺
          </el-button>
          <el-button
              type="warning"
              @click="saveAsTemplate"
              class="template-btn"
          >
            <i class="el-icon-copy"></i> 保存为模板
          </el-button>
          <el-button
              type="primary"
              @click="openTemplateList"
              class="template-list-btn"
          >
            <i class="el-icon-files"></i> 模板列表
          </el-button>
          <el-button
              type="info"
              @click="restoreDefault"
              class="default-btn"
          >
            <i class="el-icon-refresh"></i> 恢复默认
          </el-button>
          <el-button
              type="success"
              @click="saveSettings"
              :loading="saving"
              class="save-btn"
              :disabled="!selectedShopId"
          >
            <i class="el-icon-check"></i> 保存设置
          </el-button>
          <el-button
              type="primary"
              @click="previewSettings"
              class="preview-btn"
          >
            <i class="el-icon-view"></i> 预览效果
          </el-button>
        </div>
      </div>
    </div>

    <!-- 当前应用范围提示 -->
    <div class="application-scope" v-if="selectedShopId">
      <i class="el-icon-info-circle"></i>
      <span>当前设置将应用于：{{ currentShopName || '所有店铺' }}</span>
    </div>

    <!-- 未选择店铺提示 -->
    <div class="no-shop-selected" v-if="!selectedShopId">
      <i class="el-icon-warning-outline"></i>
      <span>请先选择要装修的店铺</span>
    </div>

    <!-- 主要内容区 -->
    <div class="content-wrapper" v-if="selectedShopId !== null">
      <!-- 左侧设置面板 -->
      <div class="settings-panel">
        <!-- 轮播图设置 -->
        <div class="setting-card">
          <div class="section-header">
            <i class="el-icon-picture section-icon"></i>
            <h2 class="section-title">首页轮播图</h2>
            <p class="section-info">最多可添加3张轮播图，支持JPG、PNG格式</p>
          </div>

          <div class="carousel-settings">
            <div class="carousel-list">
              <div v-for="(slide, index) in carouselImages" :key="index" class="carousel-item">
                <div class="carousel-preview">
                  <img :src="slide.url" :alt="`轮播图 ${index + 1}`" class="carousel-img" v-if="slide.url">
                  <div class="placeholder-img" v-else>
                    <i class="el-icon-picture-outline"></i>
                    <span>轮播图 {{ index + 1 }}</span>
                  </div>
                  <div class="carousel-actions">
                    <el-button icon="el-icon-top" size="mini" @click="moveSlideUp(index)" :disabled="index === 0"
                               class="action-btn"></el-button>
                    <el-button icon="el-icon-bottom" size="mini" @click="moveSlideDown(index)"
                               :disabled="index === carouselImages.length - 1" class="action-btn"></el-button>
                    <el-button icon="el-icon-delete" size="mini" type="danger" @click="deleteSlide(index)"
                               class="action-btn"></el-button>
                  </div>
                </div>
                <div class="carousel-link-setting">
                  <el-input v-model="slide.link" placeholder="点击轮播图跳转链接（可选）" size="small"></el-input>
                </div>
              </div>
            </div>

            <!-- 添加轮播图按钮 -->
            <div v-if="carouselImages.length < 3">
              <el-upload
                  class="upload-carousel"
                  action="/api/qzc/petServer/file/upload"
                  :on-success="handleCarouselUploadSuccess"
                  :disabled="carouselImages.length >= 3"
                  list-type="picture-card"
                  :show-file-list="false"
                  :headers="uploadHeaders"
              >
                <i class="el-icon-plus"></i>
              </el-upload>

              <p class="upload-hint" v-if="carouselImages.length >= 3">
                <i class="el-icon-info"></i> 已达到最大轮播图数量（3张）
              </p>
            </div>
          </div>
        </div>

        <!-- 菜单栏设置 -->
        <div class="setting-card menu-card">
          <div class="section-header">
            <i class="el-icon-menu section-icon"></i>
            <h2 class="section-title">底部菜单栏</h2>
            <p class="section-info">自定义首页、商城、预约、我的四个菜单的图标和名称</p>
          </div>

          <div class="menu-settings">
            <div class="menu-grid">
              <div
                  v-for="(menu, index) in menuItems"
                  :key="menu.id"
                  class="menu-item"
              >
                <div class="menu-icon-upload">
                  <el-upload
                      class="avatar-uploader"
                      action="/api/qzc/petServer/file/upload"
                      :on-success="(response, file) => handleMenuIconUpload(response, file, index)"
                      :show-file-list="false"
                      :before-upload="beforeMenuIconUpload"
                      :headers="uploadHeaders"
                  >
                    <img
                        v-if="menu.iconUrl"
                        :src="menu.iconUrl"
                        :alt="menu.name || menu.defaultName"
                        class="menu-icon"
                    >
                    <i v-else :class="menu.defaultIcon" class="default-menu-icon avatar-uploader-icon"></i>
                  </el-upload>
                </div>

                <div class="menu-name-setting">
                  <el-input
                      v-model="menu.name"
                      :placeholder="`请输入${menu.defaultName}`"
                      size="small"
                      :maxlength="6"
                  ></el-input>
                  <p class="menu-name-hint">最多6个字符</p>
                </div>

                <div class="menu-page-setting">
                  <el-select
                      v-model="menu.pagePath"
                      placeholder="选择页面"
                      size="small"
                  >
                    <el-option
                        v-for="page in availablePages"
                        :key="page.path"
                        :label="page.name"
                        :value="page.path"
                    ></el-option>
                  </el-select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧预览区 -->
      <div class="preview-panel">
        <div class="preview-header">
          <h2 class="preview-title">实时预览</h2>
          <!-- 模板预览标记 -->
          <div v-if="isTemplatePreview" class="template-preview-tag">
            <i class="el-icon-eye"></i> 模板预览中
          </div>
        </div>

        <!-- 手机预览容器 -->
        <div class="device-preview-container">
          <div class="phone-frame">
            <div class="phone-header">
              <div class="phone-camera"></div>
            </div>
            <div class="phone-content">
              <!-- 预览轮播图 -->
              <el-carousel
                  height="200px"
                  v-if="(isTemplatePreview && previewTemplate.carouselImages && previewTemplate.carouselImages.length > 0) ||
                      (!isTemplatePreview && carouselImages.length > 0)"
                  class="preview-carousel"
              >
                <el-carousel-item
                    v-for="(slide, index) in isTemplatePreview ? previewTemplate.carouselImages : carouselImages"
                    :key="index"
                >
                  <img
                      :src="slide.url"
                      :alt="`轮播图 ${index + 1}`"
                      class="preview-carousel-img"
                      v-if="slide.url"
                  >
                  <div class="preview-placeholder-img" v-else>
                    <i class="el-icon-picture-outline"></i>
                  </div>
                </el-carousel-item>
              </el-carousel>

              <!-- 预览菜单 -->
              <div class="preview-menu-bar">
                <div
                    v-for="menu in isTemplatePreview ? previewTemplate.menuItems : menuItems"
                    :key="menu.id"
                    class="preview-menu-item"
                >
                  <div class="preview-menu-icon">
                    <img
                        v-if="menu.iconUrl"
                        :src="menu.iconUrl"
                        :alt="menu.name || menu.defaultName"
                    >
                    <i v-else :class="menu.defaultIcon" class="default-menu-icon"></i>
                  </div>
                  <span class="preview-menu-name">{{ menu.name || menu.defaultName }}</span>
                </div>
              </div>
            </div>
            <div class="phone-footer"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 店铺选择弹窗 -->
    <el-dialog
        title="选择装修的店铺"
        :visible.sync="showShopSelectionDialog"
        width="50%"
    >
      <div class="shop-selection-dialog">
        <el-table
            :data="shopListWithAll"
            border
            style="width: 100%"
            @row-click="handleShopRowClick"
            :row-class-name="tableRowClassName"
        >
          <el-table-column
              prop="id"
              label="店铺ID"
              width="100"
          ></el-table-column>
          <el-table-column
              prop="name"
              label="店铺名称"
          ></el-table-column>
          <el-table-column
              prop="address"
              label="店铺地址"
          ></el-table-column>
          <el-table-column
              prop="phone"
              label="联系电话"
          ></el-table-column>
          <el-table-column
              prop="isActive"
              label="状态"
              width="80"
          >
            <template slot-scope="scope">
              <el-tag type="success" v-if="scope.row.isActive && scope.row.id !== 'all'">启用</el-tag>
              <el-tag type="danger" v-else-if="scope.row.id !== 'all'">禁用</el-tag>
              <el-tag type="info" v-else>默认</el-tag>
            </template>
          </el-table-column>
        </el-table>

        <p class="selection-hint" v-if="selectedShopId === null">
          <i class="el-icon-info"></i> 请选择一个店铺进行装修
        </p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showShopSelectionDialog = false">取消</el-button>
        <el-button type="primary" @click="confirmShopSelection" :disabled="!selectedShopId">确定</el-button>
      </span>
    </el-dialog>

    <!-- 保存为模板弹窗 -->
    <div v-if="showSaveTemplateDialog">
      <el-dialog
          title="保存为模板"
          :visible.sync="showSaveTemplateDialog"
          width="40%"
      >
        <div class="save-template-dialog">
          <el-input
              v-model="templateName"
              placeholder="请输入模板名称"
              maxlength="20"
          ></el-input>
          <p class="template-name-hint">最多20个字符</p>

          <el-input
              v-model="templateDescription"
              placeholder="请输入模板描述（可选）"
              maxlength="100"
              type="textarea"
              rows="3"
              style="margin-top: 15px;"
          ></el-input>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="showSaveTemplateDialog = false">取消</el-button>
          <el-button type="primary" @click="confirmSaveTemplate" :disabled="!templateName.trim()">确定</el-button>
        </span>
      </el-dialog>
    </div>

    <!-- 模板列表弹窗 -->
    <el-dialog
        title="模板列表"
        :visible.sync="showTemplateListDialog"
        width="80%"
        :before-close="closeTemplateList"
    >
      <div class="template-list-header">
        <p class="template-count">共 {{ templates.length }} 个模板</p>
        <el-input
            v-model="templateSearchKeyword"
            placeholder="搜索模板名称"
            prefix-icon="el-icon-search"
            size="small"
            class="template-search"
            @input="handleTemplateSearch"
        ></el-input>
      </div>

      <div class="template-list" v-if="filteredTemplates.length > 0">
        <div
            v-for="template in filteredTemplates"
            :key="template.id"
            class="template-item-card"
        >
          <div class="template-preview-container">
            <div class="template-phone-preview">
              <div class="template-phone-frame">
                <div class="template-phone-content">
                  <!-- 模板预览轮播图 -->
                  <div v-if="template.carouselImages && template.carouselImages.length > 0"
                       class="template-carousel-preview">
                    <img
                        :src="template.carouselImages[0].url"
                        :alt="template.name"
                        class="template-carousel-img"
                        v-if="template.carouselImages[0].url"
                    >
                    <div class="template-placeholder-img" v-else>
                      <i class="el-icon-picture-outline"></i>
                    </div>
                  </div>

                  <!-- 模板预览菜单 -->
                  <div class="template-menu-bar">
                    <div
                        v-for="menu in template.menuItems"
                        :key="menu.id"
                        class="template-menu-item"
                    >
                      <div class="template-menu-icon">
                        <img
                            v-if="menu.iconUrl"
                            :src="menu.iconUrl"
                            :alt="menu.name || menu.defaultName"
                            class="menu-icon-img"
                        >
                        <i v-else :class="menu.defaultIcon" class="default-menu-icon"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="template-info">
            <h3 class="template-name">{{ template.name }}</h3>
            <p class="template-desc">{{ template.description || '无描述' }}</p>
            <p class="template-created-time">创建时间: {{ formatTime(template.createdAt) }}</p>
          </div>

          <div class="template-actions">
            <el-button
                size="small"
                type="primary"
                @click="previewTemplateDetail(template)"
            >
              <i class="el-icon-view"></i> 预览
            </el-button>
            <el-button
                size="small"
                type="success"
                @click="applyTemplate(template)"
            >
              <i class="el-icon-check"></i> 应用
            </el-button>
            <el-button
                size="small"
                type="danger"
                @click="deleteTemplate(template.id)"
            >
              <i class="el-icon-delete"></i> 删除
            </el-button>
          </div>
        </div>
      </div>

      <div v-else class="no-templates">
        <i class="el-icon-folder-opened"></i>
        <p>暂无保存的模板</p>
        <el-button type="primary" @click="closeTemplateListAndSaveNew">
          <i class="el-icon-plus"></i> 创建新模板
        </el-button>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="closeTemplateList">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 应用模板确认弹窗 -->
    <el-dialog
        title="应用模板"
        :visible.sync="showApplyTemplateConfirm"
        width="40%"
    >
      <div class="apply-template-confirm">
        <i class="el-icon-question-circle"></i>
        <p>确定要应用模板"{{ applyingTemplate.name }}"吗？</p>
        <p class="apply-warning">当前设置将会被模板内容覆盖</p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showApplyTemplateConfirm = false">取消</el-button>
        <el-button type="primary" @click="confirmApplyTemplate">确定</el-button>
      </span>
    </el-dialog>

    <!-- 恢复默认确认弹窗 -->
    <el-dialog
        title="提示"
        :visible.sync="showRestoreConfirmDialog"
        width="30%"
        :show-close="false"
    >
      <div class="restore-confirm-content">
        <i class="el-icon-warning-outline"></i>
        <p>确定要恢复默认设置吗？当前设置将会被覆盖。</p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showRestoreConfirmDialog = false">取消</el-button>
        <el-button type="danger" @click="confirmRestoreDefault">确定</el-button>
      </span>
    </el-dialog>

  </div>
</template>

<script>
import request from '@/api/request'

export default {
  name: 'ShopDecoration',
  data() {
    return {
      user: this.$store.state.user,
      // 店铺相关
      shopList: [],
      selectedShopId: 'all', // 默认选择所有店铺
      selectedShopName: '所有店铺',
      currentShopName: '所有店铺',
      showShopSelectionDialog: false,

      // 模板相关
      showSaveTemplateDialog: false,
      templateName: '',
      templateDescription: '',
      showTemplateListDialog: false,
      templates: [],
      filteredTemplates: [],
      templateSearchKeyword: '',
      isTemplatePreview: false,
      previewTemplate: {},
      showApplyTemplateConfirm: false,
      applyingTemplate: {},

      // 装修配置
      carouselImages: [],
      menuItems: [
        {
          id: 1,
          name: '',
          defaultName: '首页',
          iconUrl: '',
          defaultIcon: 'el-icon-s-home',
          pagePath: '/home'
        },
        {
          id: 2,
          name: '',
          defaultName: '商城',
          iconUrl: '',
          defaultIcon: 'el-icon-shopping-bag',
          pagePath: '/mall'
        },
        {
          id: 3,
          name: '',
          defaultName: '预约',
          iconUrl: '',
          defaultIcon: 'el-icon-calendar',
          pagePath: '/reservation'
        },
        {
          id: 4,
          name: '',
          defaultName: '我的',
          iconUrl: '',
          defaultIcon: 'el-icon-user',
          pagePath: '/profile'
        }
      ],

      // 可选页面路径
      availablePages: [
        {name: '首页', path: '/home'},
        {name: '商品列表', path: '/products'},
        {name: '分类页', path: '/categories'},
        {name: '购物车', path: '/cart'},
        {name: '个人中心', path: '/profile'},
        {name: '预约页面', path: '/reservation'},
        {name: '关于我们', path: '/about'}
      ],

      // 状态控制
      saving: false,
      showRestoreConfirmDialog: false,

      // 上传相关
      uploadHeaders: {
        'Authorization': 'Bearer ' + this.$store.state.token
      },

      // 通知提示
      notification: {
        visible: false,
        title: '',
        message: '',
        type: ''
      }
    }
  },
  computed: {
    // 包含"所有店铺"选项的店铺列表
    shopListWithAll() {
      return [
        {
          id: 'all',
          name: '所有店铺',
          address: '全部店铺通用设置',
          phone: '',
          isActive: true
        },
        ...this.shopList
      ];
    }
  },
  created() {
    this.getShopList();
    this.getGlobalDecorationConfig();
  },
  methods: {
    // 打开店铺选择对话框
    openShopSelection() {
      this.showShopSelectionDialog = true;
      // 如果店铺列表为空，重新获取
      if (this.shopList.length === 0) {
        this.getShopList();
      }
    },

    // 获取店铺列表
    getShopList() {
      this.$api.shop.getShops({
        organizationId: this.user.organizationId
      }).then(response => {
        if (response.statusCode === 100200) {
          this.shopList = response.data || [];
        } else {
          this.showNotification('获取店铺列表失败', response.message, 'error');
        }
      }).catch(error => {
        console.error('获取店铺列表出错:', error);
        this.showNotification('错误', '获取店铺列表时发生错误', 'error');
      });
    },

    // 获取全局默认配置（所有店铺）
    getGlobalDecorationConfig() {
      request({
        url: '/decoration/config/global',
        method: 'get'
      }).then(response => {
        if (response.statusCode === 100200) {
          const config = response.data || {};
          this.applyConfig(config);
        } else {
          // 如果没有全局配置，使用默认值
          console.log('没有找到全局配置，使用默认值');
        }
      }).catch(error => {
        console.error('获取全局配置出错:', error);
        // 出错时保持默认值
      });
    },

    // 表格行点击事件
    handleShopRowClick(row) {
      this.selectedShopId = row.id;
      this.selectedShopName = row.name;
    },

    // 表格行样式
    tableRowClassName({row}) {
      return row.id === this.selectedShopId ? 'table-row-selected' : '';
    },

    // 确认店铺选择
    confirmShopSelection() {
      if (this.selectedShopId) {
        this.currentShopName = this.selectedShopName;
        this.showShopSelectionDialog = false;

        // 如果选择的是所有店铺，获取全局配置
        if (this.selectedShopId === 'all') {
          this.getGlobalDecorationConfig();
        } else {
          // 否则获取该店铺的装修配置
          this.getShopDecorationConfig();
        }
      }
    },

    // 获取店铺装修配置
    getShopDecorationConfig() {
      request({
        url: '/decoration/config',
        method: 'get',
        params: {shopId: this.selectedShopId}
      }).then(response => {
        if (response.statusCode === 100200) {
          const config = response.data || {};
          this.applyConfig(config);
        } else {
          this.showNotification('获取装修配置失败', response.message, 'error');
        }
      }).catch(error => {
        console.error('获取装修配置出错:', error);
        this.showNotification('错误', '获取装修配置时发生错误', 'error');
      });
    },

    // 应用配置数据
    applyConfig(config) {
      // 解析轮播图配置
      if (config.carouselConfig) {
        try {
          this.carouselImages = JSON.parse(config.carouselConfig);
        } catch (e) {
          console.error('解析轮播图配置失败:', e);
          this.carouselImages = [];
        }
      } else {
        this.carouselImages = [];
      }

      // 解析菜单配置
      if (config.menuConfig) {
        try {
          const menuConfig = JSON.parse(config.menuConfig);
          // 合并菜单配置，保留默认值
          menuConfig.forEach((menu, index) => {
            if (this.menuItems[index]) {
              this.menuItems[index] = {...this.menuItems[index], ...menu};
            }
          });
        } catch (e) {
          console.error('解析菜单配置失败:', e);
        }
      }
    },

    // 保存为模板
    saveAsTemplate() {
      this.templateName = '';
      this.templateDescription = '';
      this.showSaveTemplateDialog = true;
    },

    // 确认保存模板
    confirmSaveTemplate() {
      const template = {
        name: this.templateName.trim(),
        description: this.templateDescription.trim(),
        // 存储轮播图和菜单配置
        carouselConfig: JSON.stringify(this.carouselImages),
        menuConfig: JSON.stringify(this.menuItems)
      };

      request({
        url: '/decoration/template',
        method: 'post',
        data: template
      }).then(response => {
        if (response.statusCode === 100200) {
          this.showNotification('成功', `模板"${template.name}"保存成功`, 'success');
          this.showSaveTemplateDialog = false;
          // 重新获取模板列表
          this.getTemplateList();
        } else {
          this.showNotification('保存模板失败', response.message, 'error');
        }
      }).catch(error => {
        console.error('保存模板出错:', error);
        this.showNotification('错误', '保存模板时发生错误', 'error');
      });
    },

    // 打开模板列表
    openTemplateList() {
      this.templateSearchKeyword = '';
      this.showTemplateListDialog = true;
      this.getTemplateList();
    },

    // 获取模板列表
    getTemplateList() {
      request({
        url: '/decoration/templates',
        method: 'get'
      }).then(response => {
        if (response.statusCode === 100200) {
          this.templates = response.data || [];
          this.filteredTemplates = [...this.templates];
        } else {
          this.showNotification('获取模板列表失败', response.message, 'error');
        }
      }).catch(error => {
        console.error('获取模板列表出错:', error);
        this.showNotification('错误', '获取模板列表时发生错误', 'error');
      });
    },

    // 搜索模板
    handleTemplateSearch() {
      if (!this.templateSearchKeyword) {
        this.filteredTemplates = [...this.templates];
        return;
      }

      const keyword = this.templateSearchKeyword.toLowerCase();
      this.filteredTemplates = this.templates.filter(template =>
          template.name.toLowerCase().includes(keyword) ||
          (template.description && template.description.toLowerCase().includes(keyword))
      );
    },

    // 关闭模板列表
    closeTemplateList() {
      this.showTemplateListDialog = false;
      this.isTemplatePreview = false;
    },

    // 关闭模板列表并打开新建模板窗口
    closeTemplateListAndSaveNew() {
      this.closeTemplateList();
      this.saveAsTemplate();
    },

    // 预览模板详情
    previewTemplateDetail(template) {
      try {
        this.previewTemplate = {
          ...template,
          carouselImages: template.carouselConfig ? JSON.parse(template.carouselConfig) : [],
          menuItems: template.menuConfig ? JSON.parse(template.menuConfig) : []
        };
        this.isTemplatePreview = true;
      } catch (e) {
        console.error('解析模板配置失败:', e);
        this.showNotification('错误', '解析模板配置失败', 'error');
      }
    },

    // 申请应用模板
    applyTemplate(template) {
      this.applyingTemplate = template;
      this.showApplyTemplateConfirm = true;
    },

    // 确认应用模板
    confirmApplyTemplate() {
      // 根据当前选择的是所有店铺还是单个店铺，调用不同的API
      const url = this.selectedShopId === 'all'
          ? '/decoration/template/apply/global'
          : '/decoration/template/apply';

      const params = this.selectedShopId === 'all'
          ? {templateId: this.applyingTemplate.id}
          : {
            templateId: this.applyingTemplate.id,
            shopId: this.selectedShopId
          };

      request({
        url,
        method: 'post',
        params
      }).then(response => {
        if (response.statusCode === 100200) {
          this.showNotification('成功', `模板"${this.applyingTemplate.name}"已应用`, 'success');
          this.showApplyTemplateConfirm = false;
          this.showTemplateListDialog = false;
          this.isTemplatePreview = false;

          // 重新获取配置
          if (this.selectedShopId === 'all') {
            this.getGlobalDecorationConfig();
          } else {
            this.getShopDecorationConfig();
          }
        } else {
          this.showNotification('应用模板失败', response.message, 'error');
        }
      }).catch(error => {
        console.error('应用模板出错:', error);
        this.showNotification('错误', '应用模板时发生错误', 'error');
      });
    },

    // 删除模板
    deleteTemplate(templateId) {
      this.$confirm('确定要删除此模板吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        request({
          url: `/decoration/template/${templateId}`,
          method: 'delete'
        }).then(response => {
          if (response.statusCode === 100200) {
            this.showNotification('成功', '模板已删除', 'success');
            // 重新获取模板列表
            this.getTemplateList();

            // 如果删除的是正在预览的模板，退出预览状态
            if (this.isTemplatePreview && this.previewTemplate.id === templateId) {
              this.isTemplatePreview = false;
            }
          } else {
            this.showNotification('删除模板失败', response.message, 'error');
          }
        }).catch(error => {
          console.error('删除模板出错:', error);
          this.showNotification('错误', '删除模板时发生错误', 'error');
        });
      }).catch(() => {
        // 取消删除
      });
    },

    // 格式化时间显示
    formatTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}-${this.padZero(date.getMonth() + 1)}-${this.padZero(date.getDate())} ${this.padZero(date.getHours())}:${this.padZero(date.getMinutes())}`;
    },

    // 数字补零
    padZero(num) {
      return num < 10 ? `0${num}` : num;
    },

    // 恢复默认功能
    restoreDefault() {
      this.showRestoreConfirmDialog = true;
    },

    // 确认恢复默认
    confirmRestoreDefault() {
      // 恢复默认设置
      this.carouselImages = [];
      this.menuItems = [
        {
          id: 1,
          name: '',
          defaultName: '首页',
          iconUrl: '',
          defaultIcon: 'el-icon-s-home',
          pagePath: '/home'
        },
        {
          id: 2,
          name: '',
          defaultName: '商城',
          iconUrl: '',
          defaultIcon: 'el-icon-shopping-bag',
          pagePath: '/mall'
        },
        {
          id: 3,
          name: '',
          defaultName: '预约',
          iconUrl: '',
          defaultIcon: 'el-icon-calendar',
          pagePath: '/reservation'
        },
        {
          id: 4,
          name: '',
          defaultName: '我的',
          iconUrl: '',
          defaultIcon: 'el-icon-user',
          pagePath: '/profile'
        }
      ];

      this.showRestoreConfirmDialog = false;
      this.showNotification('成功', '已恢复默认设置', 'success');
    },

    // 轮播图上传成功处理
    handleCarouselUploadSuccess(response) {
      if (response.statusCode === 100200 && response.data) {
        this.carouselImages.push({
          url: response.data,
          link: ''
        });
      } else {
        this.showNotification('上传失败', '轮播图上传失败', 'error');
      }
    },

    // 菜单图标上传前校验
    beforeMenuIconUpload(file) {
      const isImage = file.type.indexOf('image/') === 0;
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isImage) {
        this.showNotification('格式错误', '只能上传图片文件！', 'warning');
        return false;
      }
      if (!isLt2M) {
        this.showNotification('大小超限', '上传图片大小不能超过 2MB！', 'warning');
        return false;
      }

      return true;
    },

    // 菜单图标上传成功处理
    handleMenuIconUpload(response, file, index) {
      if (response.statusCode === 100200 && response.data) {
        this.menuItems[index].iconUrl = response.data;
      } else {
        this.showNotification('上传失败', '菜单图标上传失败', 'error');
      }
    },

    // 上移轮播图
    moveSlideUp(index) {
      if (index > 0) {
        [this.carouselImages[index], this.carouselImages[index - 1]] =
            [this.carouselImages[index - 1], this.carouselImages[index]];
      }
    },

    // 下移轮播图
    moveSlideDown(index) {
      if (index < this.carouselImages.length - 1) {
        [this.carouselImages[index], this.carouselImages[index + 1]] =
            [this.carouselImages[index + 1], this.carouselImages[index]];
      }
    },

    // 删除轮播图
    deleteSlide(index) {
      this.carouselImages.splice(index, 1);
      this.showNotification('成功', '轮播图已删除', 'success');
    },

    // 保存设置
    saveSettings() {
      this.saving = true;

      const url = this.selectedShopId === 'all'
          ? '/decoration/config/global'
          : '/decoration/config';

      const decorationParam = this.selectedShopId === 'all'
          ? {
            carouselConfig: JSON.stringify(this.carouselImages),
            menuConfig: JSON.stringify(this.menuItems)
          }
          : {
            shopId: this.selectedShopId,
            carouselConfig: JSON.stringify(this.carouselImages),
            menuConfig: JSON.stringify(this.menuItems)
          };

      request({
        url,
        method: 'post',
        data: decorationParam
      }).then(response => {
        this.saving = false;
        if (response.statusCode === 100200) {
          this.showNotification('成功', '店铺装修设置已保存', 'success');
        } else {
          this.showNotification('保存失败', response.message, 'error');
        }
      }).catch(error => {
        this.saving = false;
        console.error('保存设置出错:', error);
        this.showNotification('错误', '保存设置时发生错误', 'error');
      });
    },

    // 预览设置
    previewSettings() {
      // 这里可以实现打开新窗口预览的逻辑
      this.showNotification('提示', '正在打开预览窗口...', 'info');
    },

    // 显示通知
    showNotification(title, message, type) {
      this.$notify({
        title: title,
        message: message,
        type: type,
        duration: 3000
      });
    }
  }
}
</script>

<style scoped>
/* 基础样式 */
.shop-decoration-container {
  padding: 24px;
  background-color: #f5f7fa;
  min-height: 100vh;
  box-sizing: border-box;
  font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* 顶部导航区 */
.top-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8e8e8;
}

.page-header {
  flex: 1;
  min-width: 280px;
}

.page-title {
  font-size: 24px;
  color: #1f2d3d;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.page-desc {
  font-size: 14px;
  color: #8392a5;
  margin: 0;
}

.action-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  min-width: 280px;
}

/* 操作按钮区 */
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

/* 按钮样式优化 */
.shop-select-btn,
.template-btn,
.template-list-btn,
.default-btn,
.save-btn,
.preview-btn {
  transition: all 0.2s ease;
  border-radius: 4px;
}

.shop-select-btn:hover,
.template-btn:hover,
.template-list-btn:hover,
.default-btn:hover,
.save-btn:hover,
.preview-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* 应用范围提示 */
.application-scope {
  background-color: #e6f7ff;
  border-left: 4px solid #1890ff;
  padding: 12px 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  font-size: 14px;
  color: #1890ff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 未选择店铺提示 */
.no-shop-selected {
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 12px 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  font-size: 14px;
  color: #856404;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 主要内容区 */
.content-wrapper {
  display: flex;
  gap: 24px;
  height: calc(100vh - 180px);
}

/* 设置面板 */
.settings-panel {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
}

/* 设置卡片 */
.setting-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  padding: 24px;
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

.setting-card:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.menu-card {
  margin-bottom: 0;
}

/* 设置区块标题 */
.section-header {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f2f5;
}

.section-icon {
  color: #1890ff;
  font-size: 20px;
}

.section-title {
  font-size: 16px;
  color: #1f2d3d;
  font-weight: 500;
  margin: 0;
}

.section-info {
  font-size: 12px;
  color: #8392a5;
  margin: 0;
  flex: 1;
  text-align: right;
}

/* 轮播图设置 */
.carousel-list {
  margin-bottom: 24px;
}

.carousel-item {
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 6px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.carousel-item:hover {
  background-color: #f0f7ff;
  border-color: #91d5ff;
}

.carousel-preview {
  position: relative;
  margin-bottom: 16px;
  border-radius: 4px;
  overflow: hidden;
  height: 180px;
  background-color: #f1f1f1;
  border: 1px dashed #e5e6eb;
}

.carousel-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.placeholder-img {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8c8c8c;
}

.placeholder-img i {
  font-size: 36px;
  margin-bottom: 10px;
}

.carousel-item:hover .carousel-img {
  transform: scale(1.02);
}

.carousel-actions {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 8px;
}

.action-btn {
  border-color: rgba(204, 204, 204, 0.5);
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 4px;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.carousel-link-setting {
  width: 100%;
}

/* 上传区域 */
.upload-carousel {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  padding: 36px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fafafa;
}

.upload-carousel:hover {
  border-color: #1890ff;
  background-color: #f0f7ff;
}

.upload-carousel .el-icon-plus {
  font-size: 28px;
  color: #8c8c8c;
}

.upload-text {
  margin-top: 12px;
  font-size: 14px;
  color: #8c8c8c;
}

.upload-hint {
  margin-top: 12px;
  font-size: 12px;
  color: #8392a5;
  display: flex;
  align-items: center;
  gap: 5px;
  justify-content: center;
}

/* 菜单设置 */
.menu-settings {
  padding-top: 8px;
}

.menu-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.menu-item {
  background-color: #f9f9f9;
  border-radius: 6px;
  padding: 20px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.menu-item:hover {
  background-color: #f0f7ff;
  border-color: #91d5ff;
}

.menu-icon-upload {
  margin-bottom: 18px;
  display: flex;
  justify-content: center;
}

.avatar-uploader {
  width: 90px;
  height: 90px;
}

.menu-icon {
  width: 90px;
  height: 90px;
  border-radius: 6px;
  object-fit: contain;
  background-color: #fff;
  padding: 12px;
  border: 1px solid #eee;
  transition: transform 0.3s ease;
}

.default-menu-icon {
  font-size: 24px;
  color: #8c8c8c;
}

.avatar-uploader-icon {
  width: 90px;
  height: 90px;
  line-height: 90px;
  font-size: 28px;
  color: #8c8c8c;
  background-color: #fff;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-item:hover .menu-icon {
  transform: scale(1.05);
}

.menu-name-setting {
  margin-bottom: 16px;
}

.menu-name-hint {
  font-size: 12px;
  color: #8392a5;
  margin-top: 6px;
  text-align: right;
}

/* 预览面板 */
.preview-panel {
  width: 380px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  padding: 24px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}

.preview-panel:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f2f5;
}

.preview-title {
  font-size: 16px;
  color: #1f2d3d;
  font-weight: 500;
  margin: 0;
}

.template-preview-tag {
  background-color: #f5f3ff;
  color: #722ed1;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 设备预览容器 */
.device-preview-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 10px 0;
}

/* 手机预览框 */
.phone-frame {
  width: 320px;
  height: 600px;
  border: 12px solid #1a1a1a;
  border-radius: 40px;
  position: relative;
  overflow: hidden;
  transform: scale(0.95);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.phone-frame:hover {
  transform: scale(1);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
}

.phone-header {
  height: 44px;
  background-color: #1a1a1a;
  position: relative;
}

.phone-camera {
  width: 120px;
  height: 22px;
  background-color: #1a1a1a;
  border-radius: 0 0 20px 20px;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}

.phone-content {
  height: calc(100% - 88px);
  overflow: hidden;
  background-color: #f5f5f5;
  position: relative;
}

.phone-footer {
  height: 44px;
  background-color: #1a1a1a;
  position: relative;
}

.phone-footer::after {
  content: '';
  position: absolute;
  width: 120px;
  height: 5px;
  border-radius: 3px;
  background-color: rgba(255, 255, 255, 0.3);
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

/* 预览轮播图 */
.preview-carousel {
  width: 100%;
}

.preview-carousel-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-placeholder-img {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #eee;
  color: #8c8c8c;
}

.preview-placeholder-img i {
  font-size: 36px;
}

/* 预览菜单 */
.preview-menu-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-around;
  background-color: #fff;
  padding: 12px 0 8px;
  border-top: 1px solid #eee;
}

.preview-menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  transition: transform 0.2s ease;
  padding: 5px 0;
}

.preview-menu-item:active {
  transform: scale(0.95);
}

.preview-menu-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
}

.preview-menu-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.preview-menu-name {
  font-size: 12px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60px;
  text-align: center;
}

/* 店铺选择弹窗 */
.shop-selection-dialog {
  max-height: 500px;
  overflow-y: auto;
}

.table-row-selected {
  background-color: #f0f7ff;
}

/* 模板列表样式 */
.template-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f2f5;
}

.template-count {
  color: #8392a5;
  font-size: 14px;
  margin: 0;
}

.template-search {
  width: 300px;
}

.template-list {
  max-height: 500px;
  overflow-y: auto;
  padding-right: 10px;
}

.template-item-card {
  display: flex;
  align-items: center;
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  border: 1px solid #f0f2f5;
}

.template-item-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.template-preview-container {
  flex: 0 0 200px;
}

.template-phone-preview {
  display: flex;
  justify-content: center;
}

.template-phone-frame {
  width: 140px;
  height: 260px;
  border: 8px solid #1a1a1a;
  border-radius: 24px;
  position: relative;
  overflow: hidden;
}

.template-phone-content {
  height: 100%;
  background-color: #f5f5f5;
  position: relative;
  overflow: hidden;
}

.template-carousel-preview {
  height: 80px;
  overflow: hidden;
}

.template-carousel-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.template-placeholder-img {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #eee;
  color: #8c8c8c;
}

.template-menu-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-around;
  background-color: #fff;
  padding: 8px 0 4px;
  border-top: 1px solid #eee;
}

.template-menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.template-menu-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-icon-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.template-info {
  flex: 1;
  padding: 0 20px;
}

.template-name {
  font-size: 16px;
  color: #1f2d3d;
  margin: 0 0 8px 0;
  font-weight: 500;
}

.template-desc {
  font-size: 14px;
  color: #8392a5;
  margin: 0 0 8px 0;
  line-height: 1.5;
}

.template-created-time {
  font-size: 12px;
  color: #c9cdD4;
  margin: 0;
}

.template-actions {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
}

.no-templates {
  text-align: center;
  padding: 60px 0;
  color: #8392a5;
}

.no-templates i {
  font-size: 48px;
  margin-bottom: 16px;
  color: #c9cdD4;
}

.no-templates p {
  margin: 0 0 20px 0;
  font-size: 16px;
}

/* 应用模板确认弹窗 */
.apply-template-confirm {
  padding: 20px 0;
  text-align: center;
}

.apply-template-confirm i {
  font-size: 36px;
  color: #faad14;
  margin-bottom: 16px;
}

.apply-template-confirm p {
  font-size: 16px;
  margin: 0 0 10px 0;
}

.apply-warning {
  color: #f5222d;
  font-size: 14px !important;
}

/* 弹窗样式优化 */
.shop-selection-dialog, .save-template-dialog {
  padding: 10px 0;
}

.shop-select-dialog {
  min-width: 300px;
}

.selection-hint {
  margin-top: 10px;
  font-size: 12px;
  color: #faad14;
  display: flex;
  align-items: center;
  gap: 5px;
}

.template-name-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #8392a5;
  text-align: right;
}

.restore-confirm-content, .success-message {
  text-align: center;
  padding: 20px 0;
}

.restore-confirm-content i {
  font-size: 32px;
  color: #faad14;
  margin-bottom: 15px;
}

.success-message i {
  font-size: 48px;
  color: #52c41a;
  margin-bottom: 15px;
}

.success-message p {
  font-size: 16px;
  color: #333;
  margin: 0 0 10px 0;
}

.apply-to-info {
  font-size: 14px;
  color: #666;
  margin-top: 10px !important;
}

/* 滚动条美化 */
.settings-panel::-webkit-scrollbar,
.template-list::-webkit-scrollbar,
.shop-selection-dialog::-webkit-scrollbar {
  width: 6px;
}

.settings-panel::-webkit-scrollbar-track,
.template-list::-webkit-scrollbar-track,
.shop-selection-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.settings-panel::-webkit-scrollbar-thumb,
.template-list::-webkit-scrollbar-thumb,
.shop-selection-dialog::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.settings-panel::-webkit-scrollbar-thumb:hover,
.template-list::-webkit-scrollbar-thumb:hover,
.shop-selection-dialog::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式优化 */
@media (max-width: 1200px) {
  .content-wrapper {
    height: auto;
    min-height: calc(100vh - 180px);
  }

  .template-item-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .template-preview-container {
    margin-bottom: 16px;
  }

  .template-info {
    padding: 0 0 16px 0;
  }

  .template-actions {
    align-self: flex-end;
  }
}

@media (max-width: 992px) {
  .content-wrapper {
    flex-direction: column;
  }

  .preview-panel {
    width: 100%;
    margin-bottom: 24px;
    max-width: none;
  }

  .device-preview-container {
    padding: 20px 0;
  }
}

@media (max-width: 768px) {
  .menu-grid {
    grid-template-columns: 1fr;
  }

  .top-bar {
    flex-direction: column;
    align-items: flex-start;
    padding-bottom: 12px;
  }

  .action-controls {
    width: 100%;
  }

  .action-buttons {
    width: 100%;
    justify-content: flex-start;
  }

  .shop-decoration-container {
    padding: 16px;
  }

  .template-search {
    width: 100%;
    margin-top: 10px;
  }

  .template-list-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 576px) {
  .phone-frame {
    width: 260px;
    height: 500px;
    border-width: 10px;
    border-radius: 32px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .shop-select-btn, .template-btn, .template-list-btn, .default-btn, .save-btn, .preview-btn {
    width: 100%;
  }

  .section-info {
    display: none;
  }

  .section-header {
    padding-bottom: 8px;
  }

  .setting-card {
    padding: 16px;
  }
}
</style>
